<template>
    <v-avatar :color="color" :size="size">
        <v-img v-if="src" :src="src" cover/>
        <span v-else class="font-weight-medium text-white" :style="{ fontSize, lineHeight: 1 }">{{ initials }}</span>
    </v-avatar>
</template>

<script setup>
import {computed} from 'vue';

// Shared avatar for people. Shows the uploaded picture when there is one,
// otherwise the person's initials on a coloured circle. Used everywhere a
// user shows up (profile, header, user list, dialogs, notes) so the
// image-or-initials behaviour stays consistent in one place.
const props = defineProps({
    src: {type: String, default: null},
    firstName: {type: String, default: ''},
    lastName: {type: String, default: ''},
    // Full name as a single string, used when first/last are not available
    // separately (e.g. a "John Doe" field). First/last take precedence.
    name: {type: String, default: ''},
    email: {type: String, default: ''},
    size: {type: [String, Number], default: 'default'},
    color: {type: String, default: 'primary'},
});

const initials = computed(() => {
    const first = (props.firstName || '').trim();
    const last = (props.lastName || '').trim();
    let pair = (first.charAt(0) + last.charAt(0)).trim();
    if (!pair && props.name) {
        const parts = props.name.trim().split(/\s+/).filter(Boolean);
        pair = ((parts[0]?.charAt(0) || '') + (parts.length > 1 ? parts[parts.length - 1].charAt(0) : '')).trim();
    }
    if (pair) return pair.toUpperCase();
    const email = (props.email || '').trim();
    return (email.charAt(0) || '?').toUpperCase();
});

// Scale the text to the avatar so two letters fit at any size. Vuetify does
// not size text content for us, so a small avatar would otherwise clip a
// default-size font.
const sizePx = computed(() => {
    const named = {'x-small': 16, 'small': 24, 'default': 40, 'large': 48, 'x-large': 56};
    if (typeof props.size === 'number') return props.size;
    if (named[props.size] != null) return named[props.size];
    const parsed = parseInt(props.size, 10);
    return isNaN(parsed) ? 40 : parsed;
});

const fontSize = computed(() => `${Math.max(9, Math.round(sizePx.value * 0.42))}px`);
</script>
